import { useTranslation } from "react-i18next";

import { Gradients } from "components/gradients";
import { useAnimatedValue } from "hooks/use-animated-value";
import { useGauge } from "hooks/use-gauge";
import { useStore } from "hooks/use-store";
import { PLACEHOLDER } from "utils/constants";
import { Color } from "utils/theme";
import {
  MAIN_ARC_BG,
  MAIN_ARC_STROKE,
  OUTER_ARC_BG,
  OUTER_ARC_STROKE,
} from "components/gauge-style";
import { SvgGradients } from "types/enums";

// Constants
const START_ANGLE = 45;
const END_ANGLE = 135;
const MAX_VALUE = 6.0;
const DIAMETER = 450;
const ARC_WIDTH = 30;
const STROKE_CAP = "round";
const MIN_IMPACT_TO_SHOW = 0.1;

export const ImpactGauge = () => {
  const { t } = useTranslation();

  // Store values
  const impact = useStore((state) => state.impact);
  const config = useStore((state) => state.config);

  // Animated value for smooth transitions
  const animatedImpact = useAnimatedValue(impact);

  // Determine the maximum value for the gauge dynamically
  const gaugeMaxValue = Math.max(
    MAX_VALUE,
    config?.max_impact ?? 3,
    animatedImpact
  );

  // Gauge setup
  const gauge = useGauge({
    domain: [0, gaugeMaxValue],
    startAngle: START_ANGLE,
    endAngle: END_ANGLE,
    numTicks: 18,
    diameter: DIAMETER,
  });

  const label = t("dashboard.impact");
  const limit = config?.max_impact ?? 3;
  const isAboveLimit = animatedImpact >= limit;
  const limitAngle =
    limit > MAX_VALUE
      ? gauge.valueToAngle(MAX_VALUE)
      : gauge.valueToAngle(limit);

  return (
    <svg
      {...gauge.getSVGProps(ARC_WIDTH * 2)}
      preserveAspectRatio="xMidYMid meet"
      className="w-full h-full"
    >
      {/* Defs */}
      <Gradients />

      {/* Main arc */}
      <g id="main-arc">
        <path
          id="main-arc-stroke"
          {...gauge.getArcProps({
            offset: 0,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={MAIN_ARC_STROKE}
          strokeWidth={ARC_WIDTH * 1.4}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="main-arc-bg"
          {...gauge.getArcProps({
            offset: 0,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={MAIN_ARC_BG}
          strokeWidth={ARC_WIDTH * 1.3}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="main-arc-value"
          {...gauge.getArcProps({
            offset: 0,
            startAngle: START_ANGLE,
            endAngle:
              impact !== null && impact > gaugeMaxValue
                ? gauge.valueToAngle(gaugeMaxValue)
                : gauge.valueToAngle(
                    animatedImpact >= MIN_IMPACT_TO_SHOW ? animatedImpact : 0
                  ),
          })}
          fill="none"
          stroke={`url(#${isAboveLimit ? SvgGradients.Red : SvgGradients.Blue}`}
          strokeWidth={animatedImpact > 0 ? ARC_WIDTH : 0}
          strokeLinecap={STROKE_CAP}
        />
      </g>

      {/* Outer arc */}
      <g id="outer-arc">
        <path
          id="outer-arc-stroke"
          {...gauge.getArcProps({
            offset: ARC_WIDTH * 1.1,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={OUTER_ARC_STROKE}
          strokeWidth={ARC_WIDTH * 0.4}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="outer-arc-bg"
          {...gauge.getArcProps({
            offset: ARC_WIDTH * 1.1,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={OUTER_ARC_BG}
          strokeWidth={ARC_WIDTH * 0.3}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="outer-arc-value"
          {...gauge.getArcProps({
            offset: ARC_WIDTH * 1.1,
            startAngle: limitAngle,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={
            isAboveLimit
              ? `url(#${SvgGradients.Red})`
              : `url(#${SvgGradients.RedFaded})`
          }
          opacity={1}
          strokeWidth={ARC_WIDTH * 0.3}
          strokeLinecap={STROKE_CAP}
        />
      </g>

      {/* Center value and label */}
      <g id="center">
        <text
          x={"-40%"}
          fill={isAboveLimit ? `url(#${SvgGradients.Red})` : Color.White}
          className="text-[5rem] font-bold font-mono"
          alignmentBaseline="middle"
          dominantBaseline="middle"
          textAnchor="start"
        >
          {impact !== null ? animatedImpact.toFixed(1) : PLACEHOLDER}
        </text>
        <text
          x={"-40%"}
          y={"13%"}
          fill={isAboveLimit ? Color.Rose[600] : Color.Primary[500]}
          className="text-2xl font-semibold"
          alignmentBaseline="middle"
          dominantBaseline="middle"
          textAnchor="start"
        >
          {label}
        </text>
      </g>
    </svg>
  );
};
